import { useCallback } from "react";
import { useAppShell } from "@salestrak/ui";

/**
 * Returns a callback that dismisses the tier's navigation overlay — the
 * compact drawer or the medium flyout — and no-ops on the full tier, whose
 * pinned pane has nothing to dismiss. Use for actions that LEAVE the
 * sidebar: firing an RPC, an external link, toggling a surface the user
 * wants the pane out of the way for. Menu-based interactions auto-close with
 * their own overlay, and route navigation auto-closes via
 * `NavOverlayAutoClose` in the workspace layout. An action that opens a
 * DIALOG uses {@link useCloseSidebarIfCompact} instead: the dialog opens over
 * the medium pane, which stays however the user left it.
 *
 * The overlays are the shell's own `openMobile`/`openOverlay` state, so this
 * file is a thin named seam over `useAppShell()` rather than a provider of
 * its own.
 */
export function useCloseSidebarIfMobile(): () => void {
  const { tier, setOpenMobile, setOpenOverlay } = useAppShell();
  return useCallback(() => {
    if (tier === "compact") setOpenMobile(false);
    else if (tier === "medium") setOpenOverlay(false);
  }, [tier, setOpenMobile, setOpenOverlay]);
}

/**
 * Returns a callback that dismisses the compact drawer only, no-op on every
 * other tier. This is the close for an action that opens a DIALOG or overlay
 * from inside the sidebar — a favorite, New Project, New Workflow, Share. The
 * compact drawer covers the whole screen, so the dialog the user asked for is
 * where they are going and the drawer has nothing left to show. The medium
 * flyout is sticky by contract (see `openOverlay` in the shell context): a
 * user who expanded it should not have to re-open it after every dialog, so
 * it is left alone, exactly as the full tier's pinned pane is.
 */
export function useCloseSidebarIfCompact(): () => void {
  const { tier, setOpenMobile } = useAppShell();
  return useCallback(() => {
    if (tier === "compact") setOpenMobile(false);
  }, [tier, setOpenMobile]);
}
